import type { JobArtifactView, JobView } from '@detachly/contracts'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '../lib/api-context'
import { navigateTo } from '../lib/browser'
import { describeError } from '../lib/errors'
import { artifactLabel, formatBytes, formatRelative, jobLabel } from '../lib/format'
import { Button } from './ui'

/**
 * Downloads go through a short-lived signed URL: the click asks the API for one, then hands the
 * browser straight to storage. We never proxy the bytes, and the link dies within minutes.
 */
export function ArtifactRow({
  artifact,
  job,
  stale,
}: {
  artifact: JobArtifactView
  job: Pick<JobView, 'id' | 'type'>
  /** True when the project was scanned again after this file was built. */
  stale?: boolean
}) {
  const api = useApi()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const download = async () => {
    setBusy(true)
    setError(null)
    try {
      const { url } = await api.downloadArtifact(artifact.id)
      navigateTo(url)
    } catch (cause: unknown) {
      setError(cause)
    } finally {
      setBusy(false)
    }
  }

  return (
    <tr>
      <td>
        <div className="step__name">{artifactLabel(artifact.kind)}</div>
        {error ? (
          <div className="field__error" role="alert">
            {describeError(error).title}
          </div>
        ) : null}
      </td>
      <td className="muted">
        {jobLabel(job.type)}
        {stale ? <div className="subtle">Built before the last scan</div> : null}
      </td>
      <td className="muted">{formatBytes(artifact.bytes)}</td>
      <td className="muted">{artifact.expiresAt ? formatRelative(artifact.expiresAt) : '—'}</td>
      <td className="table__actions">
        {/* A report is meant to be read; the download is the copy that travels with the zip. */}
        {artifact.kind === 'report_md' ? (
          <Link to={`/app/jobs/${job.id}/report`} className="button button--secondary">
            Read
          </Link>
        ) : null}
        <Button variant="secondary" onClick={() => void download()} disabled={busy}>
          {busy ? 'Preparing…' : 'Download'}
        </Button>
      </td>
    </tr>
  )
}
